/**
 * EventCard - Display card for a single tour stop/event
 */

import { Link } from 'react-router-dom';
import type { TourStop } from '../../api/events';
import { formatDateRange } from '../../api/events';

interface EventCardProps {
  event: TourStop;
  /** Location query string (zip/lat/lng/q) for pricing context */
  locationQuery?: string;
}

export function EventCard({ event, locationQuery }: EventCardProps) {
  const isSoldOut = event.status === 'sold_out';
  const isCancelled = event.status === 'cancelled';

  const linkUrl = locationQuery
    ? `/events/${event.id}?${locationQuery}`
    : `/events/${event.id}`;

  const distance = event.distance_miles ?? event.straight_line_miles;

  return (
    <article className="card group hover:shadow-lg transition-shadow duration-200">
      {/* Show Logo / Image — wrapped in Link so clicking the image opens the event */}
      <Link
        to={linkUrl}
        className="relative block aspect-[3/2] bg-gradient-to-br from-primary-100 to-primary-50 rounded-lg overflow-hidden mb-4"
        tabIndex={-1}
        aria-hidden="true"
      >
        {event.show.logo_url ? (
          <img
            src={event.show.logo_url}
            alt={`${event.show.name} logo`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl">🎭</span>
          </div>
        )}

        {/* Status Badge */}
        {isSoldOut && (
          <div className="absolute top-3 right-3 bg-danger-500 text-white text-xs font-semibold px-2 py-1 rounded">
            SOLD OUT
          </div>
        )}
        {isCancelled && (
          <div className="absolute top-3 right-3 bg-warm-500 text-white text-xs font-semibold px-2 py-1 rounded">
            CANCELLED
          </div>
        )}

        {/* Distance Badge */}
        {distance !== undefined && (
          <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-warm-700 text-xs font-medium px-2 py-1 rounded shadow-sm">
            ~{Math.round(distance)} mi away
          </div>
        )}
      </Link>

      {/* Content */}
      <div>
        <p className="text-sm text-primary-600 italic mb-1">
          with a cast member from
        </p>

        <h3 className="text-xl font-display font-bold text-warm-900 mb-1 group-hover:text-primary-600 transition-colors">
          <Link to={linkUrl} className="hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded">
            {event.show.name}
          </Link>
        </h3>

        <p className="text-warm-600 mb-2">
          {event.theater.name}
        </p>

        <p className="text-warm-500 text-sm mb-3">
          {event.city.name}, {event.city.state}
        </p>

        <div className="flex items-center gap-2 text-sm text-warm-600 mb-4">
          <svg
            className="h-4 w-4 text-primary-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>{formatDateRange(event.start_date, event.end_date)}</span>
        </div>

        {/* CTA */}
        {!isCancelled && (
          <Link
            to={linkUrl}
            className={`btn w-full text-center ${
              isSoldOut
                ? 'btn-outline text-warm-600 border-warm-300 hover:bg-warm-50'
                : 'btn-primary'
            }`}
          >
            {isSoldOut ? 'Join Waitlist' : 'View Details'}
          </Link>
        )}
      </div>
    </article>
  );
}

export default EventCard;
